import React, { useEffect } from "react";
import CardSection from "components/cardSection/CardSection";
import { PutCapture, PutCancelPaymentIntent } from "api/rpc/2024-04/facilityAdmin/order/transaction";
import { StatusCode } from "api/protocols";
import {
  PaymentIntent,
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElementChangeEvent,
} from "@stripe/stripe-js";
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import { PutCancelAccountTransaction } from "api/rpc/facilityAdmin/customer";
import { PutAccountTransactionCapture } from "api/rpc/2024-04/facilityAdmin/customer/customer";
import { PutCaptureInvoiceTransaction, PutCancelInvoiceTransaction } from "api/rpc/2024-04/facilityAdmin/order/invoice";

interface IProps {
  accountTransaction?: boolean;
  invoiceTransaction?: boolean;
  onSuccess?: (captureTransaction?: { id?: number }) => void;
  onError?: any;
  onSubmit?: any;
  transaction: any;
  trigger: boolean;
  exchangeCollect?: boolean;
  onChange?: (
    e: StripeCardNumberElementChangeEvent | StripeCardExpiryElementChangeEvent | StripeCardCvcElementChangeEvent,
  ) => void;
}

export interface IBillingDetailsState {
  name: string;
}

export async function cancelManualCreditPaymentIntent(
  paymentIntentId: string,
  transactionId: number,
  accountPayment = false,
  invoicePayment = false,
) {
  let cancelIntentRes;
  if (accountPayment) {
    //Cancel account transaction
    cancelIntentRes = await PutCancelAccountTransaction(
      { payment_intent_id: paymentIntentId, transaction_id: transactionId },
      true,
    );
  } else if (invoicePayment) {
    //Cancel accoinvoiceunt transaction
    cancelIntentRes = await PutCancelInvoiceTransaction(
      { payment_intent_id: paymentIntentId, transaction_id: transactionId },
      true,
    );
  } else {
    //Cancel order transaction
    cancelIntentRes = await PutCancelPaymentIntent(
      { payment_intent_id: paymentIntentId, transaction_id: transactionId },
      true,
    );
  }
  if (cancelIntentRes?.status !== StatusCode.OK) {
    console.log("Error cancelling payment intent");
    return false;
  }
  return true;
}

export default function CheckoutForm(props: IProps) {
  const stripe = useStripe();
  const elements = useElements();
  const { transaction, onSuccess, onError, onSubmit, trigger } = props;

  //(05/Aug/21)::(09:15:54) - TODO - Is a ref needed to access this function?

  //Handle submit trigger
  useEffect(() => {
    if (trigger) {
      void handleSubmit();
    }
  }, [trigger]);

  async function captureAuthorizedPayment(paymentIntent: PaymentIntent) {
    elements?.getElement(CardNumberElement)?.clear();
    elements?.getElement(CardExpiryElement)?.clear();
    elements?.getElement(CardCvcElement)?.clear();

    let captureResult;
    if (props?.accountTransaction) {
      captureResult = await PutAccountTransactionCapture(
        { transaction_id: transaction.id, payment_intent_id: paymentIntent.id },
        true,
      );
    } else if (props?.invoiceTransaction) {
      captureResult = await PutCaptureInvoiceTransaction(
        { transaction_id: transaction.id, payment_intent_id: paymentIntent.id },
        true,
      );
    } else {
      captureResult = await PutCapture(
        { payment_intent_id: paymentIntent.id, transaction_id: transaction.id, exchange_collect: props?.exchangeCollect ? props?.exchangeCollect : undefined },
        true,
      );
    }

    if (captureResult.status === StatusCode.OK) {
      onSuccess?.(captureResult.data as Record<string, any>);
    } else {
      onError?.(captureResult.message ?? captureResult.data?.message ?? "Failed to capture payment");
    }

    onSubmit?.();
  }

  async function handleSubmit() {
    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    if (!transaction?.client_secret) {
      return;
    }

    onSubmit?.();

    // If the intent was already authorized (e.g. prior confirm succeeded but capture failed),
    // do not call confirmCardPayment again — Stripe rejects a new payment method on requires_capture.
    const existingIntent = await stripe.retrievePaymentIntent(transaction.client_secret as string);
    if (existingIntent.error) {
      onError?.(existingIntent.error.message);
      return;
    }

    const existingStatus = existingIntent.paymentIntent?.status;
    if (existingStatus === "requires_capture") {
      await captureAuthorizedPayment(existingIntent.paymentIntent);
      return;
    }
    if (existingStatus === "succeeded") {
      onSuccess?.();
      onSubmit?.();
      return;
    }

    const result = await stripe.confirmCardPayment(transaction.client_secret as string, {
      payment_method: {
        card: elements.getElement(CardNumberElement),
      },
    });

    if (result.error) {
      onError?.(result.error.message);
      return;
    }

    if (result.paymentIntent?.status === "requires_capture") {
      await captureAuthorizedPayment(result.paymentIntent);
    } else if (result.paymentIntent?.status === "succeeded") {
      elements.getElement(CardNumberElement)?.clear();
      elements.getElement(CardExpiryElement)?.clear();
      elements.getElement(CardCvcElement)?.clear();
      onSuccess?.();
      onSubmit?.();
    }
  }

  return (
    <div className="flex flex-column">
      <CardSection onChange={props?.onChange} type="two-row" />
    </div>
  );
}
